import { useCallback, useMemo, useState } from 'react';
import { DiaryNotification, getDefaultNotificationsState, NotificationsState } from '@/src/features/app/store/notifications/state';
import { uuidv4 } from '@/src/utils/uuidv4/uuidv4';

export interface NotificationsStore {
  state: NotificationsState;

  display(notification: DiaryNotification): void;
  close(guid: string): void;
}

const AUTO_CLOSE_TIMEOUT = 7000;

export const useNotificationsStore = (): NotificationsStore => {
  const [state, setState] = useState(getDefaultNotificationsState());

  const close = useCallback((guid: string) => {
    setState(prevState => {
      const notification = prevState.notifications.find(item => item.guid === guid);

      clearTimeout(notification?.timeout);

      return {
        ...prevState,
        notifications: prevState.notifications.filter(item => item.guid !== guid),
      };
    });
  }, []);

  const display = useCallback((notification: DiaryNotification) => {
    const guid = uuidv4();

    setState(prevState => ({
      ...prevState,
      notifications: [
        ...prevState.notifications,
        {
          guid,
          ...notification,
          timeout: setTimeout(() => close(guid), AUTO_CLOSE_TIMEOUT),
        },
      ],
    }));

    setTimeout(() => {
      close(guid);
    }, AUTO_CLOSE_TIMEOUT);
  }, [close]);

  return useMemo(() => ({
    state,
    display,
    close,
  }), [
    state,
    display,
    close,
  ]);
};